import React, { useMemo } from 'react';
import {
  AccountAllocationMode,
  FinancialPlanAccounts,
  FinancialPlanData,
} from '../../types/financialPlan';
import {
  ACCOUNT_ALLOCATION_KEYS,
  applyAccountPatchWithAllocation,
  computeInvestablePoolAtAge,
  getAccountAllocationMode,
  totalAccountContributionPct,
} from '../../utils/lgcAccountAllocation';
import { formatCurrency, formatPct } from '../../utils/lifetimeGoalsCalc';
import {
  IRS_LIMITS_2026,
  SECTION_HINTS,
  BUSINESS_SEP_IRA,
} from '../../utils/financialPlanDefaults';
import { sepIraAnnualFromPct, sepIraAnnualMax, SEP_IRA_MAX_INCOME_PCT } from '../../utils/lgcSepIra';

type AccountKey = keyof FinancialPlanAccounts;

const ACCOUNT_META: Record<
  AccountKey,
  { title: string; subtitle?: string; hint?: string; showMatch?: boolean }
> = {
  k401: {
    title: BUSINESS_SEP_IRA.title,
    subtitle: `${BUSINESS_SEP_IRA.businessName} · ${BUSINESS_SEP_IRA.description}`,
    hint: `Self-managed SEP IRA through the business (${BUSINESS_SEP_IRA.businessName}). 2026 max employer contribution: $${IRS_LIMITS_2026.sepIra.toLocaleString()} (lesser of 25% of net self-employment income or IRS cap).`,
  },
  hsa: {
    title: 'HSA',
    hint: `2026 HSA family max: $${IRS_LIMITS_2026.hsaFamily.toLocaleString()}.`,
  },
  rothIra: {
    title: 'Roth IRA',
    hint: `2026 Roth IRA max: $${IRS_LIMITS_2026.rothIra.toLocaleString()} ($${IRS_LIMITS_2026.rothIraCatchUp.toLocaleString()} age 50+).`,
  },
  taxable: {
    title: 'Taxable brokerage',
  },
};

function AllocationModeToggle({
  mode,
  onChange,
}: {
  mode: AccountAllocationMode;
  onChange: (mode: AccountAllocationMode) => void;
}) {
  return (
    <div className="lgc-allocation-mode" role="radiogroup" aria-label="Contribution entry mode">
      <button
        type="button"
        className={`lgc-allocation-mode-btn${mode === 'percent' ? ' active' : ''}`}
        aria-pressed={mode === 'percent'}
        onClick={() => onChange('percent')}
      >
        % of available
      </button>
      <button
        type="button"
        className={`lgc-allocation-mode-btn${mode === 'dollar' ? ' active' : ''}`}
        aria-pressed={mode === 'dollar'}
        onClick={() => onChange('dollar')}
      >
        Fixed $
      </button>
    </div>
  );
}

function AccountAllocationCard({
  accountKey,
  account,
  mode,
  availableMonthly,
  grossIncomeTodayAnnual,
  onChange,
  compact,
}: {
  accountKey: AccountKey;
  account: FinancialPlanAccounts[AccountKey];
  mode: AccountAllocationMode;
  availableMonthly: number;
  grossIncomeTodayAnnual: number;
  onChange: (patch: Partial<FinancialPlanAccounts[AccountKey]>) => void;
  compact?: boolean;
}) {
  const meta = ACCOUNT_META[accountKey];
  const isSepIra = accountKey === 'k401';
  const pct = account.contributionPct ?? 0;
  const monthly =
    mode === 'percent'
      ? isSepIra
        ? sepIraAnnualFromPct(grossIncomeTodayAnnual, pct) / 12
        : availableMonthly * (pct / 100)
      : account.monthlyContribution;
  const sepMaxAnnual = isSepIra ? sepIraAnnualMax(grossIncomeTodayAnnual) : 0;
  const atSepCap = isSepIra && monthly * 12 >= sepMaxAnnual - 0.5 && pct > 0;

  return (
    <div className="lgc-account-card">
      <h3 className="lgc-account-title">{meta.title}</h3>
      {!compact && meta.subtitle && <p className="lgc-account-subtitle">{meta.subtitle}</p>}
      {!compact && meta.hint && <p className="lgc-hint">{meta.hint}</p>}

      <div className="lgc-field-grid lgc-field-grid--2">
        <div className="lgc-field">
          <label className="lgc-field-label" htmlFor={`${accountKey}-bal`}>
            Current balance
          </label>
          <div className="lgc-field-input-wrap">
            <span className="lgc-field-affix">$</span>
            <input
              id={`${accountKey}-bal`}
              type="number"
              className="lgc-field-input"
              min={0}
              value={account.balance || ''}
              onChange={(e) => onChange({ balance: Math.max(0, parseFloat(e.target.value) || 0) })}
            />
          </div>
        </div>

        <div className="lgc-field">
          <label className="lgc-field-label" htmlFor={`${accountKey}-contrib`}>
            {isSepIra
              ? mode === 'percent'
                ? 'Employer contribution'
                : 'Monthly contribution'
              : mode === 'percent'
                ? 'Allocation'
                : 'Monthly contribution'}
          </label>
          <div className="lgc-field-input-wrap">
            {mode === 'percent' ? (
              <>
                <input
                  id={`${accountKey}-contrib`}
                  type="number"
                  className="lgc-field-input"
                  min={0}
                  max={isSepIra ? SEP_IRA_MAX_INCOME_PCT : 100}
                  step={0.1}
                  value={pct || ''}
                  onChange={(e) =>
                    onChange({ contributionPct: Math.max(0, parseFloat(e.target.value) || 0) })
                  }
                />
                <span className="lgc-field-affix lgc-field-affix--suffix">%</span>
              </>
            ) : (
              <>
                <span className="lgc-field-affix">$</span>
                <input
                  id={`${accountKey}-contrib`}
                  type="number"
                  className="lgc-field-input"
                  min={0}
                  value={account.monthlyContribution || ''}
                  onChange={(e) =>
                    onChange({ monthlyContribution: Math.max(0, parseFloat(e.target.value) || 0) })}
                />
              </>
            )}
          </div>
          <p
            className={`lgc-account-allocation-tracker${
              !isSepIra && mode === 'percent' && pct > 0 && availableMonthly <= 0
                ? ' lgc-account-allocation-tracker--warn'
                : ''
            }`}
          >
            <span className="lgc-mono">{formatCurrency(monthly)}/mo</span>
            {isSepIra ? (
              <>
                {' '}
                · {formatPct(pct, 1)} of {formatCurrency(grossIncomeTodayAnnual)} gross income
                {grossIncomeTodayAnnual > 0 && (
                  <> · max {formatCurrency(sepMaxAnnual)}/yr</>
                )}
                {atSepCap && <> · at IRS cap</>}
              </>
            ) : (
              mode === 'percent' && (
                <> · {formatPct(pct, 1)} of {formatCurrency(availableMonthly)} available</>
              )
            )}
            {!isSepIra && mode === 'dollar' && availableMonthly > 0 && (
              <> · {formatPct((monthly / availableMonthly) * 100, 1)} of available</>
            )}
          </p>
        </div>

        {!compact && (
          <div className="lgc-field">
            <label className="lgc-field-label" htmlFor={`${accountKey}-ret`}>
              Expected return
            </label>
            <div className="lgc-field-input-wrap">
              <input
                id={`${accountKey}-ret`}
                type="number"
                className="lgc-field-input"
                min={0}
                step={0.1}
                value={account.annualReturnPct ?? 7}
                onChange={(e) => onChange({ annualReturnPct: Math.max(0, parseFloat(e.target.value) || 0) })}
              />
              <span className="lgc-field-affix lgc-field-affix--suffix">%</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface LgcAccountsSectionProps {
  plan: FinancialPlanData;
  sourcePlan: FinancialPlanData;
  onUpdate: (patch: Partial<FinancialPlanData>) => void;
  compact?: boolean;
}

const LgcAccountsSection: React.FC<LgcAccountsSectionProps> = ({
  plan,
  sourcePlan,
  onUpdate,
  compact = false,
}) => {
  const currentAge = plan.profile.currentAge;
  const mode = compact ? 'percent' : getAccountAllocationMode(plan);
  const currentPool = useMemo(
    () => computeInvestablePoolAtAge(plan, currentAge, { isCurrentPhase: true }),
    [plan, currentAge],
  );
  const totalPct = totalAccountContributionPct(plan.accounts);

  const updateAccount = (key: AccountKey, patch: Partial<FinancialPlanAccounts[AccountKey]>) => {
    onUpdate({
      ...(compact ? { accountSettings: { allocationMode: 'percent' } } : {}),
      accounts: applyAccountPatchWithAllocation(sourcePlan, key, patch, mode),
    });
  };

  const setAllocationMode = (nextMode: AccountAllocationMode) => {
    onUpdate({ accountSettings: { allocationMode: nextMode } });
  };

  return (
    <div className={`ops-card lgc-section-card lgc-section-card--accounts${compact ? ' lgc-section-card--compact' : ''}`}>
      <div className="lgc-section-header lgc-section-header--row">
        <div className="lgc-section-header-text">
          <h2 className="lgc-section-title">{compact ? 'Accounts' : 'Tax-Advantaged Accounts'}</h2>
          {!compact && SECTION_HINTS.accounts && <p className="lgc-section-hint">{SECTION_HINTS.accounts}</p>}
        </div>
        {!compact && (
          <div className="lgc-section-header-actions">
            <AllocationModeToggle mode={mode} onChange={setAllocationMode} />
            {mode === 'percent' && (
              <span
                className={`lgc-allocation-total-pct${
                  totalPct > 100.5 ? ' lgc-allocation-total-pct--warn' : ''
                }`}
              >
                {formatPct(Math.min(999, totalPct), 1)} allocated
                {totalPct < 99.5 && (
                  <span className="lgc-allocation-unallocated">
                    {' '}
                    · {formatPct(Math.max(0, 100 - totalPct), 1)} unallocated
                  </span>
                )}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="lgc-accounts-grid">
        {ACCOUNT_ALLOCATION_KEYS.map((key) => (
          <AccountAllocationCard
            key={key}
            accountKey={key}
            account={plan.accounts[key]}
            mode={mode}
            availableMonthly={currentPool.availableToInvestMonthly}
            grossIncomeTodayAnnual={currentPool.grossIncomeTodayAnnual}
            onChange={(patch) => updateAccount(key, patch)}
            compact={compact}
          />
        ))}
      </div>

      {!compact && (
        <div className="lgc-accounts-footnotes">
          <p className="lgc-hint">
            SEP IRA employer contributions are set as a % of gross business income (max 25% or $
            {IRS_LIMITS_2026.sepIra.toLocaleString()}/yr). HSA, Roth, and taxable allocations apply to
            take-home remaining after living, debt, 529, K-12, health, lifestyle, and SEP. They do not
            need to total 100% — unallocated income is not routed into those accounts.
          </p>
          <p className="lgc-hint">
            Roth max ${IRS_LIMITS_2026.rothIra.toLocaleString()}/yr · SEP IRA ${IRS_LIMITS_2026.sepIra.toLocaleString()}/yr · HSA family ${IRS_LIMITS_2026.hsaFamily.toLocaleString()}/yr
          </p>
        </div>
      )}
    </div>
  );
};

export default LgcAccountsSection;
